import React, { Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import Router from "./routes/routes";
import { RootState } from "./redux/store";
import Header from "./components/header/header";
import Sidebar from "./components/sidebar/Sidebar";
import useBusinessMenu from "./query/useBusinessMenu";
import { canAccess } from "./utils/permissions";
import { Helmet } from "react-helmet-async";
import { GlobalPageLoaderProvider } from "./contexts/global-page-loader-context";

const MainApp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoggedIn, userData } = useSelector((state: RootState) => state.user);

  const getDefaultAuthorizedPath = () => {
    const userId = userData?.user_id ?? "";
    const candidates = [
      { path: "/market-analysis", permission: "market_analysis" as const },
      { path: userId ? `/list-clients/${userId}` : "", permission: "user_list" as const },
      { path: userId ? `/active-users/${userId}` : "", permission: "user_list" as const },
      { path: "/current-bets", permission: "current_bets" as const },
      { path: "/account-statement", permission: "account_statement" as const },
    ];

    const matched = candidates.find((candidate) => candidate.path && canAccess(userData, candidate.permission));

    return matched?.path ?? "/403";
  };

  useEffect(() => {
    if (!isLoggedIn && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    } else if (location.pathname === "/") {
      if (userData && !userData?.is_password_changed) {
        navigate("/reset-password");
      } else if (isLoggedIn) {
        navigate(getDefaultAuthorizedPath(), { replace: true });
      }
    }
  }, [isLoggedIn, location.pathname, userData]);

  useBusinessMenu({ enabled: true });

  const standalonePages = [
    "/login",
    "/reset-password",
    "/password-success",
    "/403",
    "/unauthorized",
    "/2fa-auth/google",
    "/2fa-auth/telegram",
  ];
  const isStandalonePage = standalonePages.includes(location.pathname);

  if (!isLoggedIn || isStandalonePage) {
    return (
      <Fragment>
        <Router />
      </Fragment>
    );
  }

  return (
    <GlobalPageLoaderProvider>
      <div id="layout-wrapper">
        <Header />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <Router />
          </div>
        </div>
      </div>
    </GlobalPageLoaderProvider>
  );
};

const App = () => {
  const { artifacts, isArtifactsLoading } = useSelector((state: RootState) => state.app);

  return (
    <Fragment>
      <Helmet>
        <title>{artifacts?.business}</title>

        {artifacts?.favicon && (
          <>
            <link rel="icon" href={artifacts?.favicon} />
            <link rel="apple-touch-icon" href={artifacts?.favicon} />
          </>
        )}
      </Helmet>

      {isArtifactsLoading || !artifacts ? null : <MainApp />}
    </Fragment>
  );
};

export default App;
